// Printable BEAD Draw Packet — one reimbursement draw, formatted the way a
// State Broadband Office reviewer expects. Browser-print-to-PDF.
// Driven by a real staged draw (bead_draw) via /bead/draws/:id/packet.

import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';

const fmtUSD = (n: number) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const pad3 = (n: number) => String(n).padStart(3, '0');

export default function ReimbursementPacketPage() {
  const { drawId } = useParams();

  const { data: pk, isLoading } = useQuery<any>({
    queryKey: ['draw-packet', drawId],
    queryFn: () => api.get(`/bead/draws/${drawId}/packet`),
    enabled: !!drawId,
  });

  if (isLoading || !pk) return <div style={{ padding: 24 }}>Loading packet…</div>;

  const fedPct = (Number(pk.award.fed_share_pct) * 100).toFixed(1);

  return (
    <div style={{
      maxWidth: 800, margin: '0 auto', padding: '40px 50px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: 12, color: '#0f172a', lineHeight: 1.5,
    }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff; }
          @page { margin: 0.5in; }
        }
        table { border-collapse: collapse; width: 100%; }
        th, td { padding: 8px 10px; text-align: left; }
        thead { background: #1e293b; color: #fff; }
        thead th { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; font-weight: 700; }
        tbody tr:nth-child(odd) { background: #f8fafc; }
      `}</style>

      <div className="no-print" style={{ marginBottom: 20, padding: 10, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, textAlign: 'center' }}>
        Use your browser's <b>Print → Save as PDF</b> to export this packet.
        <button onClick={() => window.print()} style={{ marginLeft: 12, padding: '4px 12px', background: '#0f172a', color: '#fff', border: 0, borderRadius: 3, cursor: 'pointer' }}>Print</button>
      </div>

      {/* Cover */}
      <div style={{ borderBottom: '3px solid #0f172a', paddingBottom: 14, marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em' }}>BEAD Subgrantee Reimbursement Request</div>
        <h1 style={{ fontSize: 26, margin: '6px 0 0 0', fontWeight: 800 }}>Draw Packet #{pad3(pk.draw.number || 1)}</h1>
        <div style={{ marginTop: 8, color: '#475569', fontSize: 13 }}>
          {pk.draw.phase_name ? <>Phase: <b>{pk.draw.phase_name}</b> · </> : null}
          {pk.draw.passings?.toLocaleString()} passings service-available · Period: {pk.period.start} – {pk.period.end}
        </div>
      </div>

      <table style={{ marginBottom: 20 }}>
        <tbody>
          <tr>
            <td style={{ width: '30%', color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Project Code</td>
            <td>{pk.project.project_code}</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Subgrantee</td>
            <td>{pk.project.prime_contractor ?? '—'}</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Program</td>
            <td>{pk.project.program_round ?? 'BEAD'} · {pk.project.state}{pk.project.county ? ` · ${pk.project.county} County` : ''}</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Awarded BSLs</td>
            <td>{Number(pk.project.awarded_bsl_count).toLocaleString()}</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Total Project Cost</td>
            <td>{fmtUSD(pk.award.total)}</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Federal Award (BEAD)</td>
            <td>{fmtUSD(pk.award.federal)} ({fedPct}%)</td>
          </tr>
          <tr>
            <td style={{ color: '#64748b', fontWeight: 600, background: '#f8fafc' }}>Subgrantee Match</td>
            <td>{fmtUSD(pk.award.match)} ({(100 - Number(fedPct)).toFixed(1)}%)</td>
          </tr>
        </tbody>
      </table>

      {/* Summary box */}
      <div style={{ background: '#0f172a', color: '#fff', padding: 16, borderRadius: 4, marginBottom: 24 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: '#94a3b8', marginBottom: 8 }}>This Draw Request</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: '#cbd5e1' }}>Eligible costs</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{fmtUSD(pk.totals.cost)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#86efac' }}>Federal share requested</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>{fmtUSD(pk.totals.federal)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#fcd34d' }}>Match applied</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#fbbf24' }}>{fmtUSD(pk.totals.match)}</div>
          </div>
        </div>
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #334155', fontSize: 11, color: '#cbd5e1', display: 'flex', justifyContent: 'space-between' }}>
          <span>Cumulative federal drawn (all requests): <b style={{ color: '#fff' }}>{fmtUSD(pk.cumulative.drawn_federal)}</b> of {fmtUSD(pk.cumulative.federal_award)} ({pk.cumulative.pct}%)</span>
          <span>Status: <b style={{ color: '#fff' }}>{pk.draw.status}</b>{pk.draw.payment_status === 'paid' ? ' · paid' : ''}</span>
        </div>
      </div>

      <h2 style={{ fontSize: 14, marginTop: 8, marginBottom: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#334155', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>
        Cost Reconciliation by Category
      </h2>

      <table style={{ marginBottom: 20 }}>
        <thead>
          <tr>
            <th>Category</th>
            <th style={{ textAlign: 'right' }}>Eligible cost</th>
            <th style={{ textAlign: 'right' }}>Federal claim</th>
            <th style={{ textAlign: 'right' }}>Match</th>
          </tr>
        </thead>
        <tbody>
          {pk.categories.map((c: any) => (
            <tr key={c.category}>
              <td style={{ fontWeight: 600 }}>{c.category}</td>
              <td style={{ textAlign: 'right' }}>{fmtUSD(c.cost)}</td>
              <td style={{ textAlign: 'right', color: '#047857', fontWeight: 600 }}>{fmtUSD(c.federal)}</td>
              <td style={{ textAlign: 'right', color: '#b45309' }}>{fmtUSD(c.match)}</td>
            </tr>
          ))}
          <tr style={{ borderTop: '2px solid #0f172a', fontWeight: 700, background: '#f1f5f9' }}>
            <td>TOTAL</td>
            <td style={{ textAlign: 'right' }}>{fmtUSD(pk.totals.cost)}</td>
            <td style={{ textAlign: 'right', color: '#047857' }}>{fmtUSD(pk.totals.federal)}</td>
            <td style={{ textAlign: 'right', color: '#b45309' }}>{fmtUSD(pk.totals.match)}</td>
          </tr>
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginBottom: 8 }}>
{pk.basis === 'actual'
          ? 'Category breakdown reflects ACTUAL invoiced costs allocated to this phase, sourced from the accounting system.'
          : "Category allocation reflects standard FTTP construction proportions applied to this draw's estimated cost — replaced by actual invoice lines as they post."}
      </div>

      <h2 style={{ fontSize: 14, marginTop: 24, marginBottom: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#334155', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 }}>
        Supporting Documentation
      </h2>
      <ul style={{ fontSize: 12, color: '#475569', paddingLeft: 20 }}>
        <li>Proof of Passings — {pk.draw.passings?.toLocaleString()} locations reported service-available to the State{pk.draw.ready_reference ? ` (ref ${pk.draw.ready_reference})` : ''}</li>
        <li>Bill of Materials reconciliation (per-segment, per-part)</li>
        <li>Crew time logs and work-order labor allocation</li>
        <li>Subcontractor invoices (HDD, splicing, locating) — from QuickBooks Online</li>
        <li>Permit and ROW fee documentation</li>
        <li>Engineering vendor invoices</li>
        <li>Photo evidence with geotag for each completed plant segment</li>
        <li>BABA / Davis-Bacon / civil rights compliance attestations (signed)</li>
      </ul>
      <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
        All supporting documentation is indexed against the line items above and available on request. Generated automatically from project records in Milestone Build.
      </div>

      {/* Sign-off block */}
      <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid #e2e8f0' }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#334155', marginBottom: 16 }}>Subgrantee Certification</h2>
        <p style={{ fontSize: 11, color: '#475569', marginBottom: 28 }}>
          The undersigned certifies that the costs reported above were actually incurred, are properly chargeable to the BEAD subgrant agreement, comply with applicable federal and state requirements (including BABA, Davis-Bacon, and civil rights provisions), and have not been previously reimbursed.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, marginTop: 30 }}>
          <div>
            <div style={{ borderBottom: '1px solid #0f172a', height: 32 }} />
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>Authorized Signature</div>
          </div>
          <div>
            <div style={{ borderBottom: '1px solid #0f172a', height: 32 }} />
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>Date</div>
          </div>
          <div>
            <div style={{ borderBottom: '1px solid #0f172a', height: 32 }} />
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>Printed Name &amp; Title</div>
          </div>
          <div>
            <div style={{ borderBottom: '1px solid #0f172a', height: 32 }} />
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>Subgrantee Organization</div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 40, fontSize: 9, color: '#94a3b8', textAlign: 'center', borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
        Generated by Milestone Build · Draw {pk.draw.id}
      </div>
    </div>
  );
}
